import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboard';
import { AnalyticsData } from '../../types';

const AnalyticsPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState('');

  const low = analytics?.risk_counts.Low ?? 0;
  const medium = analytics?.risk_counts.Medium ?? 0;
  const high = analytics?.risk_counts.High ?? 0;

  const dominantRisk =
    high >= medium && high >= low ? 'High' :
    medium >= low ? 'Medium' : 'Low';

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated) {
        setAnalytics(null);
        setError('');
        return;
      }

      try {
        const data = await dashboardService.getAnalytics();
        setAnalytics(data);
        setError('');
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load analytics data.');
      }
    };

    load();
  }, [isAuthenticated]);

  return (
    <section className="feature-page analytics-premium">
      <div className="analytics-hero">
        <div className="analytics-hero-copy">
          <p className="analytics-kicker">Insight Intelligence</p>
          <h1>Analytics</h1>
          <p>Overview of biodiversity risk trends and confidence scores.</p>
        </div>

        <div className="analytics-hero-metrics" aria-label="Analytics snapshot">
          <article className="analytics-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◌</span> Total Predictions</h3>
            <p>{analytics?.total_predictions ?? 0}</p>
          </article>
          <article className="analytics-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◍</span> Avg Confidence</h3>
            <p>{((analytics?.average_confidence ?? 0) * 100).toFixed(1)}%</p>
          </article>
          <article className="analytics-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◈</span> Dominant Risk</h3>
            <p>{dominantRisk}</p>
          </article>
          <article className="analytics-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◎</span> High Risk Count</h3>
            <p>{high}</p>
          </article>
        </div>
      </div>

      {error && <p className="form-error">{error}</p>}

      {!isAuthenticated && (
        <div className="panel guest-cta-panel">
          <h2>Analytics Requires Sign In</h2>
          <p>
            Sign in to load your analytics data and confidence trends. Guest mode can still browse public pages.
          </p>
          <div className="guest-cta-actions">
            <Link to="/login" className="auth-link-btn auth-link-btn-ghost">
              Sign in
            </Link>
            <Link to="/signup" className="auth-link-btn">
              Sign up
            </Link>
          </div>
        </div>
      )}

      <div className="stats-grid">
        <article className="panel">
          <h2>Total Predictions</h2>
          <p className="metric">{analytics?.total_predictions ?? 0}</p>
        </article>
        <article className="panel">
          <h2>Average Confidence</h2>
          <p className="metric">{((analytics?.average_confidence ?? 0) * 100).toFixed(1)}%</p>
        </article>
        <article className="panel">
          <h2>Risk Counts</h2>
          <p>Low: {low}</p>
          <p>Medium: {medium}</p>
          <p>High: {high}</p>
        </article>
      </div>
    </section>
  );
};

export default AnalyticsPage;
